import classNames from 'classnames';
import type { FC } from 'react';

import Button from '@/components/Button';
import Icon from '@/components/Icon';
import Text from '@/components/Text';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from '@/components/ui/dialog';
import type { ExternalCandidateList } from '@/services/externalCandidates';
import { capitalizeFirstLetter } from '@/utils/cvGenerator';
import { documentDateFormatter } from '@/utils/documentDateFormatter';

import SendMessage from '../../SendMessage';

type Props = {
  messages: ExternalCandidateList['messages'];
  candidateId: string;
};

const MessagesDialog: FC<Props> = ({ messages, candidateId }) => {
  return (
    <Dialog>
      <DialogTrigger className="flex w-full flex-row items-center gap-1">
        <Icon icon="material-symbols:send-rounded" />
        {messages.length}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>Messages</DialogHeader>
        <Text className="text-neutral-1000">
          {!messages.length
            ? 'No messages have been sent yet for this candidate'
            : 'Sent messages'}
        </Text>
        {messages.map(({ content, createdAt, id, type, status }) => (
          <div
            key={id}
            className="flex flex-col gap-2 overflow-hidden rounded-lg border-b bg-neutral-200 text-neutral-1000 shadow"
          >
            <div
              className={classNames(
                'flex flex-row justify-between gap-0 px-4 py-1 shadow',
                {
                  'bg-gradient-to-l from-[#885AFD] to-[#00193F]':
                    status === 'READY',
                  'bg-error': status === 'FAILED',
                  'bg-neutral-500 animate-pulse': status === 'PROCESSING',
                }
              )}
            >
              <Text
                variant="body-caption"
                className="font-semibold text-neutral-200"
              >
                {capitalizeFirstLetter(type)}
              </Text>
              <Text
                variant="body-caption"
                className="font-semibold text-neutral-200"
              >
                {documentDateFormatter(createdAt)}
              </Text>
            </div>
            {status === 'FAILED' && (
              <Text variant="body-caption" className="w-full pl-4 text-error">
                Something went wrong
              </Text>
            )}
            <Text
              variant="body-small"
              className="whitespace-pre-line break-words p-4"
            >
              {content}
            </Text>
          </div>
        ))}
        <SendMessage candidates={[Number(candidateId)]}>
          <Button prefixIcon="material-symbols:send-outline">
            Send new message
          </Button>
        </SendMessage>
      </DialogContent>
    </Dialog>
  );
};

export default MessagesDialog;
